import Icon from '@/components/ui/AppIcon';
import CTAButton from '@/components/common/CTAButton';
import { useLanguage } from '@/contexts/LanguageContext';


interface ProductOption {
  id: number;
  icon: string;
  badge: string;
  title: string;
  description: string;
  priceLabel: string;
  priceAmount: string;
  features: string[];
  ctaText: string;
  ctaLink: string;
  highlighted?: boolean;
}

interface ProductShowcaseProps {
  products: ProductOption[];
}

const ProductShowcase = ({ products }: ProductShowcaseProps) => {
  const { t } = useLanguage();

  return (
    <section className="py-20 lg:py-28 bg-secondary relative overflow-hidden">
      <div className="mx-auto max-w-content px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16 lg:mb-20">
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-primary mb-6 tracking-tight">
            {t('home.products.title')}
          </h2>
          <p className="font-body text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            {t('home.products.subtitle')}
          </p>
        </div>

        {/* Product Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 max-w-6xl mx-auto">
          {products.map((product) =>
          <div
            key={product.id}
            className={`
                group bg-white rounded-2xl p-10 lg:p-12 shadow-luxury-md border-2 transition-luxury
                ${product.highlighted ?
            'border-accent hover:shadow-gold-glow scale-105' :
            'border-border/50 hover:shadow-luxury-lg'}
                hover:-translate-y-1 relative
              `}>

              {/* Highlighted Badge */}
              {product.highlighted &&
            <div className="absolute -top-5 left-1/2 -translate-x-1/2 bg-gradient-gold text-accent-foreground px-8 py-3 rounded-full shadow-luxury-md border-4 border-white">
                  <span className="font-body text-sm font-bold tracking-wide">{t('home.products.popular')}</span>
                </div>
            }

              {/* Icon & Badge */}
              <div className="flex items-center gap-5 mb-8">
                <div className="w-20 h-20 bg-gradient-to-br from-accent/10 to-accent/5 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:shadow-gold-glow transition-luxury">
                  <Icon
                  name={product.icon as any}
                  size={36}
                  className="text-accent"
                  variant="outline" />

                </div>
                <div className="bg-primary/5 px-5 py-2.5 rounded-lg">
                  <span className="font-body text-sm font-semibold text-primary tracking-wide">
                    {product.badge}
                  </span>
                </div>
              </div>

              {/* Title */}
              <h3 className="font-heading text-3xl font-bold text-primary mb-4 tracking-tight">
                {product.title}
              </h3>

              {/* Description */}
              <p className="font-body text-lg text-muted-foreground mb-8 leading-relaxed">
                {product.description}
              </p>

              {/* Price */}
              <div className="mb-8 pb-8 border-b border-border/50">
                <p className="font-body text-sm text-muted-foreground mb-3 uppercase tracking-wider">
                  {product.priceLabel}
                </p>
                <p className="font-heading text-5xl font-bold text-gradient-gold">
                  {product.priceAmount}
                </p>
              </div>

              {/* Features */}
              <ul className="space-y-4 mb-10">
                {product.features.map((feature, index) =>
              <li key={index} className="flex items-start gap-4">
                    <Icon
                  name="CheckCircleIcon"
                  size={22}
                  className="text-success flex-shrink-0 mt-0.5"
                  variant="solid" />

                    <span className="font-body text-base text-foreground leading-relaxed">
                      {feature}
                    </span>
                  </li>
              )}
              </ul>

              {/* CTA Button */}
              <CTAButton
              href={product.ctaLink}
              variant={product.highlighted ? 'primary' : 'secondary'}
              className="w-full py-4 text-base font-semibold shadow-luxury-sm hover:shadow-luxury-md">
                {product.ctaText}
              </CTAButton>
            </div>
          )}
        </div>
      </div>
    </section>);

};

export default ProductShowcase;